import { Actions } from 'vuex-smart-module';
import { CbcState } from '@/store/cbc/state';
import { CbcGetters } from '@/store/cbc/getters';
import { CbcMutations } from '@/store/cbc/mutations';
import Axios, { AxiosError } from 'axios';
import { ServiceContainer, WebsiteService } from '@/services';
import { CreditRequest } from '@/types/cbc';
import { captureException, setTag } from '@sentry/browser';

interface CbcActionsDependencies {
	services: ServiceContainer;
}

export class CbcActions extends Actions<
	CbcState,
	CbcGetters,
	CbcMutations,
	CbcActions
	> {
	self = this;

	websiteService!: WebsiteService

	$setDependencies(dependencies: CbcActionsDependencies): void {
		this.self.websiteService = dependencies.services.website;
	}

	processError(payload?: AxiosError): Promise<void> {
		if (Axios.isCancel(payload)) {
			return Promise.resolve();
		}

		this.mutations.unsetLoading();

		this.mutations.setCbcError();

		setTag('info', 'cbcProcessError');
		captureException(payload);

		return Promise.resolve();
	}

	async getCreditVerificationCode(): Promise<void> {
		this.mutations.setLoading();
		const response = await this.websiteService.fetchVerificationCode()
			.catch(error => this.actions.processError(error));

		if (response) {
			this.mutations.unsetLoading();
			this.mutations.setCbcVerificationCode(response);
		}

		return Promise.resolve();
	}

	async getCbcCreditScore(data: CreditRequest): Promise<void> {
		this.mutations.setLoading();
		const response = await this.websiteService.fetchCbcCreditScore(data)
			.catch(error => {
				this.actions.processError(error);
				this.mutations.unsetLoading();
				this.mutations.setCbcError();
				this.mutations.unsetCbcSuccess();
				this.mutations.unsetIsCreditScoreUpdated();
			});

		if (response) {
			this.mutations.unsetLoading();
			this.mutations.unsetCbcError();
			this.mutations.setCbcSuccess();
		}

		if (response && response.score) {
			this.mutations.setCbcSuccessResponse(response);
			this.mutations.setIsCreditScoreUpdated();
		}

		return Promise.resolve();
	}

	unsetCbcErrorMessage() {
		this.mutations.unsetCbcError();
	}

	unsetCbcSuccessMessage() {
		this.mutations.unsetCbcSuccess();
	}

	setCbcErrorMessage() {
		this.mutations.setCbcError();
	}

	setLoading() {
		return this.mutations.setLoading();
	}
}
